import { BaseNode } from "./base-node";
import type { Effect, EffectPass } from "@/effects/types";
import type { Mask } from "@/masks/types";
import type { BlendMode, ColorAdjust, Transform } from "@/rendering";
import type { RetimeConfig, TransitionType, VisualElement } from "@/timeline";
import type { SeamRole } from "@/transitions/registry";

export interface VisualNodeParams {
	duration: number;
	timeOffset: number;
	trimStart: number;
	trimEnd: number;
	retime?: RetimeConfig;
	// Freeze frame (B3): hold the source frame at `trimStart` for the whole clip
	// instead of advancing the source with clip time. Video clips only.
	freeze?: boolean;
	// Color adjustment (B5): brightness/contrast/saturation applied to the source
	// frame via a Canvas2D filter before compositing. Video + image clips.
	colorAdjust?: ColorAdjust;
	// Transition seam (B4): set only on the synthetic thin nodes that scene-builder
	// emits across a transition window. `role` is which side of the cut this node
	// renders; the renderer derives seam progress from this node's own
	// timeOffset/duration and applies the per-side visual. `cacheKey` gives the
	// node an independent video sink so both seam sides can sample one source.
	seam?: { type: TransitionType; role: SeamRole };
	cacheKey?: string;
	transform: Transform;
	animations?: VisualElement["animations"];
	opacity: number;
	blendMode?: BlendMode;
	effects?: Effect[];
	masks?: Mask[];
}

export interface ResolvedVisualNodeState {
	localTime: number;
	transform: Transform;
	opacity: number;
	effectPasses: EffectPass[][];
}

export interface ResolvedVisualSourceNodeState extends ResolvedVisualNodeState {
	source: CanvasImageSource;
	sourceWidth: number;
	sourceHeight: number;
}

export abstract class VisualNode<
	Params extends VisualNodeParams = VisualNodeParams,
	Resolved extends ResolvedVisualNodeState = ResolvedVisualNodeState,
> extends BaseNode<Params, Resolved> {}
